<template>
<div>
    <div v-if="title || !hideControls" class="d-flex" style="justify-content: space-between;">
        <h3 v-if="title">{{ title }}</h3>

        <div v-if="!hideControls" class="mt-1">
            <div class="text-right mb-1">
                <button class="btn btn-outline-primary btn-collapse-xl" :disabled="zoom >= 400" @click.prevent="zoom = zoom * 1.25">
                    <i class="far fa-fw fa-search-plus"></i>
                    <span class="sr-only">Zoom In</span>
                </button>
                <button class="btn btn-outline-primary btn-collapse-xl ml-1" :disabled="zoom <= 100" @click.prevent="zoom = zoom / 1.25">
                    <i class="far fa-fw fa-search-minus"></i>
                    <span class="sr-only">Zoom Out</span>
                </button>
                <button v-if="url" class="btn btn-outline-primary btn-collapse-rs ml-1" @click.prevent="print()">
                    <i class="far fa-fw fa-file-pdf"></i>
                    <span class="btn-text ml-hf">PDF</span>
                </button>
            </div>
        </div>
    </div>

    <div class="pdf-viewer-wrapper" @click="onPdfClick">
        <template v-if="page">
            <VuePdfEmbed :page="page" :source="src" v-if="src" @loaded="onLoaded" :style="{ '--zoom': zoomPct }" :scale="scale" annotation-layer text-layer external-link-target="_blank" />
        </template>
        <template v-else>
            <VuePdfEmbed :source="src" v-if="src" @loaded="onLoaded" :style="{ '--zoom': zoomPct }" :scale="scale" annotation-layer text-layer />
        </template>
    </div>

    <div class="loader loader-modal" v-show="!isLoaded">
        <div class="spinner">
            <div class="lds-grid">
                <div></div><div></div><div></div>
                <div></div><div></div><div></div>
                <div></div><div></div><div></div>
            </div>
        </div>
    </div>

</div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed'
import * as nativeBridge from '@/native-bridge'

import 'vue-pdf-embed/dist/styles/annotationLayer.css'
import 'vue-pdf-embed/dist/styles/textLayer.css'

export default {
    components: {
        VuePdfEmbed,
    },
    props: ['url', 'page', 'title', 'hideControls'],
    // url can be a URL string (including a data URL), or an object in the form of pdf.js DocumentInitParameters: https://github.com/mozilla/pdf.js/blob/38287d943532eee939ceffbe6861163f93805ca7/src/display/api.js#L145
    // page may be omitted to render all pages
    data() {
        return {
            isLoaded: false,
            src: this.loadPDF(),
            zoom: 100,
            // vue-pdf-embed multiplies this by window.devicePixelRatio internally, so divide it out
            // here to keep canvas resolution (and memory) constant across devices instead of scaling
            // up further on high-DPR phones, crashing iOS Safari on longer PDFs.
            scale: 2 / (window.devicePixelRatio || 1)
        }
    },
    computed: {
        zoomPct() {
            return `${this.zoom}%`
        }
    },
    watch: {
        url() {
            this.src = this.loadPDF()
        }
    },
    methods: {
        loadPDF() {
            if (!this.url) {
                return null
            }
            this.isLoaded = false
            return this.url
        },
        onLoaded() {
            this.isLoaded = true
            this.$emit('loaded')
        },
        onPdfClick(evt) {
            const target = evt.target
            if (!target) {
                return
            }

            // Find the nearest link inside the annotation layer
            const link = target.closest && target.closest('.annotationLayer a[href]')
            if (!link) {
                return
            }

            const href = link.getAttribute('href') || link.href
            if (!href) {
                return
            }

            // Only intercept external http(s) links; let internal ones behave normally
            if (!/^https?:\/\//i.test(href)) {
                return
            }

            evt.preventDefault()
            window.open(href, '_blank', 'noopener,noreferrer')
        },
        print() {
            if (!this.url) {
                return null
            }
            let pdf_url = this.url.url ? this.url.url : this.url

            if (! this.$store.state.isInApp) {
                window.open(pdf_url) // NB: Chrome does not all navigating to a data URL
                return
            }

            this.$store.dispatch('START_LOADING')
            this.$api.get(pdf_url, false, false).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                if (!nativeBridge.postDocument({intent: 'view', bytes: resp, title: this.title})) {
                    console.error("In app, but no native host bridge found")
                }
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(this.$bus.firstError(errors))
            })
        }
    },
}
</script>

<style lang="scss" scoped>
.pdf-viewer-wrapper {
    margin-left: auto;
    margin-right: auto;
    overflow-x: auto;
    padding-left: 8px;
    padding-right: 8px;
    :deep(canvas) {
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.25);
        margin-left: auto;
        margin-right: auto;
        margin-top: 1em;
        margin-bottom: 1em;
        width: var(--zoom) !important;
        height: auto !important;
    }
}
.no-shadow .pdf-viewer-wrapper {
    :deep(canvas) {
        box-shadow: none;
    }
}
</style>
